import { AxiosInstance, AxiosResponse } from "axios"
import {
  QueryFileSaveResponse,
  QueryFileDeleteResponse,
} from "types/query.types"

/**
 * Queries API functions - Refactored to use authenticated axios instance
 * Authentication headers are handled automatically by the axios instance
 */

/**
 * Fetches the available query files for a given project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @returns Promise containing the list of available query files as a string
 * @throws Will throw an error if the API call fails.
 */
export async function getQueries(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.get<string>(
    `/available-queryfiles?id=${id}&hint_owner=${owner}`
  )
  return response
}

/**
 * Saves a query file for a given project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @param filename - The name of the file to be saved.
 * @param overwrite - Whether to overwrite the file if it exists.
 * @param content - The content of the query file to be saved.
 * @returns Promise containing the save operation response
 * @throws Will throw an error if the API call fails.
 */
export async function saveQueryfile(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  filename: string,
  overwrite: string,
  content: string
): Promise<AxiosResponse<QueryFileSaveResponse>> {
  // Encode content into binary using TextEncoder
  const encoder = new TextEncoder()
  const binaryContent = encoder.encode(content)

  const response = await axiosInstance.post<QueryFileSaveResponse>(
    `/queryfile-save?id=${id}&hint_owner=${owner}&name=${filename}&${overwrite}`,
    binaryContent,
    {
      headers: {
        "Content-Type": "application/octet-stream",
      },
    }
  )
  return response
}

/**
 * Deletes a query file for a given project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @param filename - The name of the file to be deleted.
 * @returns Promise containing the delete operation response
 * @throws Will throw an error if the API call fails.
 */
export async function deleteQueryfile(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  filename: string
): Promise<AxiosResponse<QueryFileDeleteResponse>> {
  const response = await axiosInstance.delete<QueryFileDeleteResponse>(
    `/queryfile-delete?id=${id}&hint_owner=${owner}&name=${filename}`
  )
  return response
}

/**
 * Fetches the content of a specific query file.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param owner - The owner of the project.
 * @param filename - The name of the file to be fetched.
 * @returns Promise containing the content of the query file as a string
 * @throws Will throw an error if the API call fails.
 */
export async function getQueryfile(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  filename: string
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.get<string>(
    `/queryfile-get?id=${id}&hint_owner=${owner}&name=${filename}`
  )
  return response
}

/**
 * Saves the metadata sidecar for a query file. The body is opaque text on the
 * backend (typically the client-defined SavedQueryEnvelope JSON). The query
 * file body must already exist — Repos rejects orphan sidecars.
 */
export async function saveQueryfileMeta(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  filename: string,
  content: string
): Promise<AxiosResponse<QueryFileSaveResponse>> {
  const response = await axiosInstance.post<QueryFileSaveResponse>(
    `/queryfile-meta-save?id=${id}&hint_owner=${owner}&name=${filename}`,
    content,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  )
  return response
}

/**
 * Fetches the metadata sidecar for a query file. Returns the raw JSON text
 * (the SavedQueryEnvelope) on success. Rejects with a 404 axios error if no
 * sidecar exists for the given query — callers should treat that as
 * "legacy flat script" and synthesize a `kind: "script"` envelope.
 *
 * Forces text-mode response: by default axios sniffs `application/json`
 * content-types and auto-parses, which would deliver an object to
 * `response.data`. Our envelope parser expects a string, so we keep the
 * payload opaque end-to-end.
 */
export async function getQueryfileMeta(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  filename: string
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.get<string>(
    `/queryfile-meta-get?id=${id}&hint_owner=${owner}&name=${filename}`,
    {
      responseType: "text",
      transformResponse: [(data: unknown) =>
        typeof data === "string" ? data : JSON.stringify(data),
      ],
    }
  )
  return response
}

/** Shape of one entry in the bulk metadata response. The `meta` field is the
 *  raw sidecar JSON parsed (so the envelope shape lives directly under it). */
export interface QueryfileMetaListEntry {
  name: string
  meta: unknown
}

/**
 * Bulk-fetches every saved query's metadata sidecar in one round-trip. Skips
 * queries that don't have a sidecar (legacy flat scripts). Used to populate
 * the saved-queries picker / list without paying N×(body+sidecar) GETs —
 * scripts can be large, so we only fetch the body when the user actually
 * opens a query into the editor.
 */
export async function getQueryfileMetaList(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<AxiosResponse<QueryfileMetaListEntry[]>> {
  const response = await axiosInstance.get<QueryfileMetaListEntry[]>(
    `/queryfile-meta-list?id=${id}&hint_owner=${owner}`
  )
  return response
}
